import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import moment from 'moment';
import { Approval, ApprovalStatus } from 'api/approval';
import { Timestamp } from 'api/models';
import { TaskStatusType } from 'api/status-updates';
import { StatusGroup, FieldOption } from 'api/tasks';
import { Task } from 'data/tasks';
import { StatusField } from 'stores/fields';

const isTaskOverdue = (
  date: [Timestamp | null, Timestamp | null],
  task: Task,
): boolean => {
  // Don't consider tasks that are due Today or in upcoming days.
  const toDate = moment(date[1]);
  const fromDate = moment(date[0]);
  const rangeEnd = toDate.isBefore(moment().startOf('d'))
    ? toDate
    : moment().subtract(1, 'days').endOf('d');

  // Move start also to correct position, otherwise start may be after the end.
  const rangeStart = fromDate.isBefore(rangeEnd) ? fromDate : rangeEnd;

  return !!(
    date[0] &&
    date[1] &&
    task.dueDate &&
    moment(task.dueDate).isBetween(rangeStart, rangeEnd) &&
    !task.completedAt
  );
};

const isTaskCompleted = (
  date: [Timestamp | null, Timestamp | null],
  task: Task,
): boolean => {
  return !!(
    date[0] &&
    date[1] &&
    task.completedAt &&
    moment(task.completedAt).isBetween(moment(date[0]), moment(date[1]))
  );
};

const isTaskBlocked = (
  date: [Timestamp | null, Timestamp | null],
  task: Task,
  blockedStatusField?: StatusField,
): boolean => {
  const taskStatusField = task.fields.find(
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    (f) => f.fieldId === blockedStatusField?.fieldId,
  );

  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const blockedFieldValue = blockedStatusField?.metaData?.options.find(
    (x) => (x as FieldOption<StatusGroup>).group === StatusGroup.Blocked,
  );

  return (
    taskStatusField?.metaFieldValue?.group ===
    (blockedFieldValue as FieldOption<StatusGroup>)?.group
  );
};

const isTaskUpcoming = (
  date: [Timestamp | null, Timestamp | null],
  task: Task,
): boolean => {
  return !!(
    date[0] &&
    date[1] &&
    task.dueDate &&
    moment(task.dueDate).isBetween(moment(date[0]), moment(date[1]))
  );
};

const isTaskInProgress = (
  date: [Timestamp | null, Timestamp | null],
  task: Task,
  progressStatusField?: StatusField,
): boolean => {
  const taskStatusField = task.fields.find(
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    (f) => f.fieldId === progressStatusField?.fieldId,
  );

  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const progressFieldValue = progressStatusField?.metaData?.options.find(
    (x) => (x as FieldOption<StatusGroup>).group === StatusGroup.InProgress,
  );

  return (
    taskStatusField?.metaFieldValue?.group ===
    (progressFieldValue as FieldOption<StatusGroup>)?.group
  );
};

export const isTaskMatchesFilter = (
  taskStatus: TaskStatusType,
  date: [Timestamp | null, Timestamp | null],
  task: Task,
  statusField?: StatusField,
): boolean => {
  switch (taskStatus) {
    case TaskStatusType.Completed:
      return isTaskCompleted(date, task);
    case TaskStatusType.Blocked:
      return isTaskBlocked(date, task, statusField);
    case TaskStatusType.Overdue:
      return isTaskOverdue(date, task);
    case TaskStatusType.Upcoming:
      return isTaskUpcoming(date, task);
    case TaskStatusType.InProgress:
      return isTaskInProgress(date, task, statusField);
    default:
      return false;
  }
};

const isTaskApprovalOverdue = (
  date: [Timestamp | null, Timestamp | null],
  approval: Approval,
): boolean => {
  return !!(
    date[0] &&
    date[1] &&
    approval.dueDate &&
    moment(approval.dueDate).isBetween(moment(date[0]), moment(date[1])) &&
    some(approval.requestTo, [
      'approvalStatus',
      ApprovalStatus.ApprovalRequested,
    ])
  );
};

const isTaskApprovalCompleted = (
  date: [Timestamp | null, Timestamp | null],
  approval: Approval,
): boolean => {
  const approvedReq = find(approval.requestTo, [
    'approvalStatus',
    ApprovalStatus.Approved,
  ]);

  return !!(
    date[0] &&
    date[1] &&
    !isEmpty(approvedReq) &&
    moment(approvedReq?.updateAt).isBetween(moment(date[0]), moment(date[1]))
  );
};

const isTaskApprovalUpcoming = (
  date: [Timestamp | null, Timestamp | null],
  approval: Approval,
): boolean => {
  return !!(
    date[0] &&
    date[1] &&
    approval.dueDate &&
    moment(approval.dueDate).isBetween(moment(date[0]), moment(date[1]))
  );
};

export const isTaskApprovalMatchesFilter = (
  taskStatus: TaskStatusType,
  date: [Timestamp | null, Timestamp | null],
  approval: Approval,
): boolean => {
  switch (taskStatus) {
    case TaskStatusType.Completed:
      return isTaskApprovalCompleted(date, approval);
    case TaskStatusType.Overdue:
      return isTaskApprovalOverdue(date, approval);
    case TaskStatusType.Upcoming:
      return isTaskApprovalUpcoming(date, approval);
    default:
      return false;
  }
};

export const isCorrectDateRange = (
  date: [Timestamp | null, Timestamp | null],
) =>
  date.every((val) => typeof val === 'number' && !Number.isNaN(val) && val > 0);
